import React from 'react';
import {
  FolderOpen,
  ChevronRight,
  Download,
  Trash2,
  Edit2,
} from 'lucide-react';
import { FileNode } from '@/lib/fileUtils';
import FileTypeIcon from '@/components/FileTypeIcon';
import { useTranslation } from 'react-i18next';

export interface FileListItemProps {
  node: FileNode;
  index: number;
  projectId: string;
  isViewer?: boolean; // 🔥 是否为 viewer 角色（只读模式）
  isFileSelected: boolean;
  isFileHovered: boolean;
  selectedFileIds: Set<string>;
  onFolderClick: (folder: FileNode) => void;
  onFilePreview: (node: FileNode, fileUrl: string) => void;
  onToggleFileSelect: (fileId: string, event: React.MouseEvent) => void;
  onDownloadFile: (file: FileNode, event: React.MouseEvent) => void;
  onDeleteFile: (file: FileNode, event: React.MouseEvent) => void;
  onEditFolder: (folder: FileNode, event: React.MouseEvent) => void;
  onMouseEnter: (node: FileNode) => void;
  onMouseLeave: () => void;
  registerRef?: (id: string, el: HTMLDivElement | null) => void;
}

export function FileListItem({
  node,
  index,
  projectId,
  isViewer = false,
  isFileSelected,
  isFileHovered,
  selectedFileIds,
  onFolderClick,
  onFilePreview,
  onToggleFileSelect,
  onDownloadFile,
  onDeleteFile,
  onEditFolder,
  onMouseEnter,
  onMouseLeave,
  registerRef,
}: FileListItemProps) {
  const { t } = useTranslation();

  const handleClick = () => {
    if (node.type === 'folder') {
      onFolderClick(node);
    }
  };

  const handleFileNameClick = () => {
    if (node.type === 'file') {
      const fileUrl = `${window.location.origin}/api/projects/${projectId}/files/content?filePath=${encodeURIComponent(node.path || node.name)}&Authorization=${localStorage.getItem('user-token')}&asAttachment=false`;
      onFilePreview(node, fileUrl);
    }
  };

  return (
    <div
      key={node.id || index}
      ref={el => {
        if (node.id && registerRef) {
          registerRef(node.id, el);
        }
      }}
      onMouseEnter={() => onMouseEnter(node)}
      onMouseLeave={onMouseLeave}
      onClick={handleClick}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg relative group ${
        isFileSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
      }`}
    >
      {/* Checkbox for file - shown before file name on hover or when selected */}
      {(isFileHovered || isFileSelected || selectedFileIds.size > 0) && (
        <div
          onClick={e => onToggleFileSelect(node.id, e)}
          className="cursor-pointer flex-shrink-0"
        >
          <div
            className={`w-4 h-4 border rounded flex items-center justify-center ${
              isFileSelected
                ? 'bg-[#2c84db] border-[#2c84db]'
                : 'border-gray-400 hover:border-gray-600 bg-white'
            }`}
          >
            {isFileSelected && (
              <svg
                className="w-2.5 h-2.5 text-white"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.5"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
        </div>
      )}

      {/* File/Folder Icon */}
      {node.type === 'folder' ? (
        <FolderOpen size={16} className="text-yellow-500 flex-shrink-0" />
      ) : (
        <FileTypeIcon fileName={node.name} size={20} />
      )}

      {/* File/Folder Name */}
      {
        <span
          onClick={handleFileNameClick}
          title={node.name}
          className="text-sm text-gray-700 truncate flex-1 cursor-pointer hover:underline hover:text-blue-600 transition-colors"
        >
          {node.name}
        </span>
      }

      {/* Action buttons (download, edit, delete) */}
      {!isFileSelected && isFileHovered && (
        <div className="flex items-center gap-1 ml-auto flex-shrink-0">
          <button
            onClick={e => onDownloadFile(node, e)}
            className="p-1 hover:bg-gray-200 rounded"
            title={
              node.type === 'folder'
                ? t('project.downloadFolder')
                : t('project.downloadFile')
            }
          >
            <Download size={14} className="text-gray-600" />
          </button>
          {!isViewer && (
            <>
              <button
                onClick={e => onEditFolder(node, e)}
                className="p-1 hover:bg-gray-200 rounded"
                title={
                  node.type === 'folder'
                    ? t('project.editFolder')
                    : t('project.editFile')
                }
              >
                <Edit2 size={14} className="text-gray-600" />
              </button>
              <button
                onClick={e => onDeleteFile(node, e)}
                className="p-1 hover:bg-gray-200 rounded"
                title={
                  node.type === 'folder'
                    ? t('project.deleteFolder')
                    : t('project.deleteFile')
                }
              >
                <Trash2 size={14} className="text-red-600" />
              </button>
            </>
          )}
        </div>
      )}
      {/* Folder chevron - only show when not hovered or selected */}
      {node.type === 'folder' && !(isFileHovered && !isFileSelected) && (
        <div className="flex items-center ml-auto flex-shrink-0">
          <ChevronRight
            size={14}
            className="text-gray-400 flex-shrink-0 ml-1"
          />
        </div>
      )}
    </div>
  );
}

export default FileListItem;
